import React from 'react';
import styled from 'styled-components';

const URL_PATTERN = /((?:https?:\/\/|www\.)[^\s<]+)/gi;

const InlineLink = styled.a<{ $inverted?: boolean }>`
  color: ${({ $inverted }) => ($inverted ? 'var(--onPrimary, #fff)' : 'var(--primary)')};
  font-weight: 700;
  text-decoration: ${({ $inverted }) => ($inverted ? 'underline' : 'none')};

  &:hover {
    text-decoration: underline;
    opacity: ${({ $inverted }) => ($inverted ? 0.92 : 1)};
  }

  &:focus-visible {
    outline: 2px solid ${({ $inverted }) => ($inverted ? 'var(--onPrimary, #fff)' : 'var(--primary)')};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

const normalizeExternalUrl = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return '';
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

const trimTrailingPunctuation = (value: string) => {
  let url = value;
  let trailing = '';
  while (/[),.!?;:]$/.test(url)) {
    trailing = `${url.slice(-1)}${trailing}`;
    url = url.slice(0, -1);
  }
  return { url, trailing };
};

type LinkedTextProps = {
  text: string;
  inverted?: boolean;
};

export const LinkedText = ({ text, inverted = false }: LinkedTextProps) => {
  const matches = Array.from(text.matchAll(URL_PATTERN));
  if (!matches.length) return <>{text}</>;

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;

  matches.forEach((match, index) => {
    const rawMatch = match[0] ?? '';
    const startIndex = match.index ?? 0;
    if (startIndex > lastIndex) {
      parts.push(text.slice(lastIndex, startIndex));
    }

    const { url, trailing } = trimTrailingPunctuation(rawMatch);
    const href = normalizeExternalUrl(url);
    parts.push(
      <InlineLink
        key={`${href}-${index}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        $inverted={inverted}
      >
        {url}
      </InlineLink>
    );
    if (trailing) {
      parts.push(trailing);
    }
    lastIndex = startIndex + rawMatch.length;
  });

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return <>{parts}</>;
};

export default LinkedText;
